import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { jwtDecode } from "jwt-decode";
import { getV2Token } from '../../utils/v2AuthUtils';
import authService from '../../services/authService';

const InactivityHandler = () => {
  const logoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleLogout = async () => {
      const token = getV2Token() || localStorage.getItem('d_c_c_t');
      if (!token) return;
      
      try {
        const responseData = await authService.logout();
        if (responseData?.success) {
          // Clear cookies and storage (same as new_demo_frontend)
          const { clearV2Token, getCookieDomain } = await import('../../utils/v2AuthUtils');
          const SecureCookieManager = (await import('../../utils/secureCookie')).default;
          // Use the same domain detection logic as new_demo_frontend
          const domain = getCookieDomain();
          clearV2Token(domain);
          SecureCookieManager.clearAuthCookies();
          localStorage.clear();
          navigate('/login');
          toast('User logged out successfully', { type: 'success' });
        } else {
          // Even if API says not successful, clear everything (same as new_demo_frontend)
          const { clearV2Token, getCookieDomain } = await import('../../utils/v2AuthUtils');
          const SecureCookieManager = (await import('../../utils/secureCookie')).default;
          // Use the same domain detection logic as new_demo_frontend
          const domain = getCookieDomain();
          clearV2Token(domain);
          SecureCookieManager.clearAuthCookies();
          localStorage.clear();
          navigate('/login');
          toast('You logged out but session is still active. Contact admin.', { type: 'error' });
        }
      } catch (error: any) {
        // Even on error, clear everything (same as new_demo_frontend)
        const { clearV2Token, getCookieDomain } = await import('../../utils/v2AuthUtils');
        const SecureCookieManager = (await import('../../utils/secureCookie')).default;
        // Use the same domain detection logic as new_demo_frontend
        const domain = getCookieDomain();
        clearV2Token(domain);
        SecureCookieManager.clearAuthCookies();
        localStorage.clear();
        navigate('/login');
        toast('You logged out but session is still active. Contact admin.', { type: 'error' });
      }
    };

    // Start the token expiration timeout (2 hours)
    const startTokenTimeout = () => {
      if (tokenTimeoutRef.current) {
        clearTimeout(tokenTimeoutRef.current);
      }

      // Check for token in cookies (v2 format) first, then localStorage
      // This matches how handleLogout gets the token
      const token = getV2Token() || localStorage.getItem('d_c_c_t');
      if (token) {
        try {
          const decoded: { exp: number } = jwtDecode(token);
          const currentTime = Math.floor(Date.now() / 1000);
          const timeLeft = (decoded.exp - currentTime) * 1000;

          if (timeLeft > 0) {
            tokenTimeoutRef.current = setTimeout(handleLogout, timeLeft);
          } else {
            handleLogout();
          }
        } catch (error) {
          handleLogout();
        }
      } else {
        // Don't logout immediately if no token - might be still loading
        // Only logout if we're sure there's no token after a delay
        console.log('[InActivityHandler] No token found, skipping token timeout setup');
      }
    };

    // Start the inactivity logout timeout (10 minutes)
    const startLogoutTimeout = () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
      logoutTimeoutRef.current = setTimeout(handleLogout, 600000); // 10 minutes
    };

    // Add event listeners to reset inactivity timeout
    const resetInactivityTimeout = () => {
      startLogoutTimeout();
    };

    const events = ['mousemove', 'keydown', 'scroll', 'click'];
    events.forEach(event => window.addEventListener(event, resetInactivityTimeout));

    // Initialize timeouts with a small delay to ensure cookies are synced
    // This prevents immediate logout when navigating from new_demo_frontend
    const initTimeout = setTimeout(() => {
      startTokenTimeout();
      startLogoutTimeout();
    }, 200); // Small delay to allow cookie optimization and token sync to complete

    // Cleanup on component unmount
    return () => {
      clearTimeout(initTimeout);
      if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
      if (tokenTimeoutRef.current) clearTimeout(tokenTimeoutRef.current);
      events.forEach(event => window.removeEventListener(event, resetInactivityTimeout));
    };
  }, [navigate]);

  return null; // This component doesn't render anything
};

export default InactivityHandler;
